import { secondsToTicks } from '@/lib/constants'

export const PLAYBACK_STORE_UPDATE_INTERVAL_MS = 67
export const PLAYBACK_INTENT_KEEPALIVE_MS = 1000
export const PLAYBACK_INTENT_SEEK_THRESHOLD_TICKS = 4000
export const PLAYBACK_INTENT_PLAYING_DRIFT_THRESHOLD_TICKS = secondsToTicks(0.25)

export type PlaybackBoundaryResult = {
  ticks: number
  shouldStop: boolean
}

export function resolvePlaybackBoundary(
  playheadTicks: number,
  direction: 1 | -1,
  sequenceEndTicks: number,
): PlaybackBoundaryResult {
  if (playheadTicks < 0) {
    return { ticks: 0, shouldStop: true }
  }

  if (direction === 1 && sequenceEndTicks > 0 && playheadTicks >= sequenceEndTicks) {
    return { ticks: sequenceEndTicks, shouldStop: true }
  }

  return { ticks: playheadTicks, shouldStop: false }
}

export type PlaybackIntentBroadcastReason = 'play-state' | 'speed' | 'seek' | 'keepalive'

type PlaybackIntentBroadcastInput = {
  playheadTicks: number
  isPlaying: boolean
  playbackSpeed: number
  lastIsPlaying: boolean
  lastPlaybackSpeed: number
  lastBroadcastTicks: number
  lastBroadcastAtMs: number
  nowMs: number
}

export function getPlaybackIntentBroadcastReason({
  playheadTicks,
  isPlaying,
  playbackSpeed,
  lastIsPlaying,
  lastPlaybackSpeed,
  lastBroadcastTicks,
  lastBroadcastAtMs,
  nowMs,
}: PlaybackIntentBroadcastInput): PlaybackIntentBroadcastReason | null {
  if (isPlaying !== lastIsPlaying) return 'play-state'
  if (playbackSpeed !== lastPlaybackSpeed) return 'speed'

  const elapsedMs = Math.max(0, nowMs - lastBroadcastAtMs)

  if (!isPlaying) {
    return Math.abs(playheadTicks - lastBroadcastTicks) > PLAYBACK_INTENT_SEEK_THRESHOLD_TICKS ? 'seek' : null
  }

  const expectedTicks = lastBroadcastTicks + secondsToTicks((elapsedMs / 1000) * playbackSpeed)
  const driftTicks = Math.abs(playheadTicks - expectedTicks)
  if (driftTicks > PLAYBACK_INTENT_PLAYING_DRIFT_THRESHOLD_TICKS) return 'seek'
  if (elapsedMs >= PLAYBACK_INTENT_KEEPALIVE_MS) return 'keepalive'

  return null
}
